import type { DrawerProps } from '@chakra-ui/react';
import {
  Button,
  DrawerBody,
  Flex,
  Icon,
  Text,
  useToast,
} from '@chakra-ui/react';
import { InDesktop } from '@ui/components/MobileOrDesktop';
import { useTranslations } from '@ui/i18n';
import { Error } from '@veno-app/icons';
import { currentWallet, getNetworkConfig } from '@veno-app/wallet';
import { useState } from 'react';

import {
  ModalOrDragDrawerContent,
  ModalOrDrawer,
  ModalOrDrawerCloseButton,
  ModalOrDrawerFooter,
  ModalOrDrawerHeader,
  ModalOrDrawerOverlay,
} from './ModalOrDrawer';

const { useIsConnected, connect } = currentWallet;

export const WalletSwitchNetworkDrawer = (
  props: Omit<DrawerProps, 'children'>,
) => {
  const t = useTranslations();
  const isConnected = useIsConnected();
  const { disconnect } = currentWallet;
  const [isSwitching, setIsSwitching] = useState(false);
  const toast = useToast();
  const networkConfig = getNetworkConfig();

  const chainSwitch = async () => {
    if (!isConnected) return;
    setIsSwitching(true);
    try {
      await connect(networkConfig);
      props.onClose();
    } catch (e: any) {
      toast({
        status: 'error',
        position: 'top',
        description: (e as Error)?.message,
      });
    } finally {
      setIsSwitching(false);
    }
  };

  const handleDisconnect = async () => {
    await disconnect();
    props.onClose();
  };

  return (
    <ModalOrDrawer placement="bottom" {...props}>
      <ModalOrDrawerOverlay zIndex="modal" />
      <ModalOrDragDrawerContent minH={{ base: '60vh', desktop: '600px' }}>
        <InDesktop>
          <ModalOrDrawerCloseButton />
        </InDesktop>
        <ModalOrDrawerHeader />
        <DrawerBody>
          <WalletSwitchContent />
        </DrawerBody>
        <ModalOrDrawerFooter>
          <Button isLoading={isSwitching} onClick={chainSwitch}>
            {t('Switch to Cronos Chain')}
          </Button>
          <Button mt={5} variant="link" onClick={handleDisconnect}>
            {t('Disconnect wallet')}
          </Button>
        </ModalOrDrawerFooter>
      </ModalOrDragDrawerContent>
    </ModalOrDrawer>
  );
};

const WalletSwitchContent = () => {
  const t = useTranslations();
  return (
    <>
      <Flex direction="column" gap="30px" align="center" textAlign="center">
        <Text textStyle="h3" color="text.normal">
          {t('Wrong network')}
        </Text>
        <Icon as={Error} color="error" boxSize="40px" />
        <Text color="text.light">
          {t('Please connect to appropriate network to use the protocol')}
        </Text>
      </Flex>
    </>
  );
};
